import React, { useEffect, useState } from 'react';
import {
  Add,
  Assignment,
  AttachMoney,
  Build,
  Business,
  CalendarToday,
  CheckCircle,
  CreditCard,
  Description,
  Download,
  Edit,
  Email,
  ExpandMore,
  Extension,
  FileCopy,
  Gavel,
  Home,
  LocalShipping,
  LocationOn,
  Message,
  Notifications,
  Person,
  Phone,
  Receipt,
  Schedule,
  Security,
  Timeline,
  Upload,
  Visibility,
  Warning,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  useCreateAddendumMutation,
  useGetAddendumsQuery,
} from '@/apis/extended/addendumApi';
import { useGetContractQuery } from '@/apis/extended/contractApi';
import {
  useGetMessagesQuery,
  useSendMessageMutation,
} from '@/apis/extended/messageApi';
import {
  useEnsurePostRatificationTasksMutation,
  useGetContractPipelineQuery,
} from '@/apis/extended/pipelineApi';
import { useGetContractTasksQuery } from '@/apis/extended/tasksApi';
import { useGetCurrentUserQuery } from '@/apis/extended/userApi';
import GlassButton from '@/components/ui/GlassButton';
import GlassCard from '@/components/ui/GlassCard';
import {
  calculateContractTimeline,
  formatTimelineDate,
  getDaysUntilDeadline,
  getOverdueCount,
  getTimelineProgress,
} from '@/utils/contractTimeline';
import { daysUntil } from '@/utils/dateOnly';
import { AddendumInterface } from '../Addendum/AddendumInterface';
import CreateAddendumForm from '../Addendum/CreateAddendumForm';
import { ActivityFeed } from './ActivityFeed';
import ClosingCoordination from './ClosingCoordination';
import CommunicationHub from './CommunicationHub';
import DocumentCenter from './DocumentCenter';
import InspectionProperty from './InspectionProperty';
import TaskManagement from './TaskManagement';

// No props needed - component will extract data from URL

interface TimelineEvent {
  id: string;
  title: string;
  description: string;
  dueDate: string;
  status: 'completed' | 'pending' | 'overdue' | 'in_progress';
  type: 'inspection' | 'financing' | 'title' | 'closing' | 'repair' | 'other';
  assignedTo: 'buyer' | 'seller' | 'agent' | 'all';
  priority: 'high' | 'medium' | 'low';
  completedDate?: string;
}

interface Task {
  id: string;
  title: string;
  description: string;
  dueDate: string;
  status: 'pending' | 'in_progress' | 'completed' | 'overdue';
  assignedTo: 'buyer' | 'seller' | 'agent';
  category: string;
  priority: 'high' | 'medium' | 'low';
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const PostAgreementDashboard: React.FC = () => {
  const theme = useTheme();
  const { contractId } = useParams<{ contractId: string }>();
  const location = useLocation();

  // Determine user role from URL path
  const getUserRoleFromPath = (): 'buyer' | 'seller' | 'agent' => {
    const path = location.pathname;
    if (path.includes('/buyer/property-dashboard/')) return 'buyer';
    if (path.includes('/seller/property-dashboard/')) return 'seller';
    if (path.includes('/agent/property-dashboard/')) return 'agent';
    return 'buyer'; // Default fallback
  };

  const userRole = getUserRoleFromPath();
  const { data: currentUser } = useGetCurrentUserQuery();
  // The role alone rendered as "Seller: Seller"; show who is signed in.
  const userName =
    currentUser?.name ||
    currentUser?.username ||
    userRole.charAt(0).toUpperCase() + userRole.slice(1);

  if (!contractId) {
    return <div>No contract ID found</div>;
  }
  const [activeTab, setActiveTab] = useState(0);
  const [extensionDialogOpen, setExtensionDialogOpen] = useState(false);
  const [extensionReason, setExtensionReason] = useState('');
  const [requestedDays, setRequestedDays] = useState(7);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [quickMessageToRole, setQuickMessageToRole] = useState<
    'buyer' | 'seller' | 'agent' | ''
  >('');
  const [addendumDialogOpen, setAddendumDialogOpen] = useState(false);

  const { data: contract, isLoading: contractLoading } =
    useGetContractQuery(contractId);
  const { data: addendumsData } = useGetAddendumsQuery(contractId);
  const { data: pipelineData } = useGetContractPipelineQuery(contractId, {
    skip: !contractId,
  });
  const [ensurePostRatificationTasks] =
    useEnsurePostRatificationTasksMutation();
  const { data: contractTasksData, isLoading: tasksQueryLoading } =
    useGetContractTasksQuery(contractId, {
      skip: !contractId,
    });
  const { data: messagesData } = useGetMessagesQuery(contractId, {
    skip: !contractId,
  });
  const [sendMessage, { isLoading: sendMessageLoading }] =
    useSendMessageMutation();
  const [createAddendum] = useCreateAddendumMutation();
  const unreadMessageCount = (messagesData?.messages || []).filter(
    (m) => !m.read
  ).length;

  // Ensure canonical post-ratification tasks exist so the dashboard is determinative (not seeded).
  useEffect(() => {
    if (!contractId) return;
    if (!contract?.isRatified) return;
    ensurePostRatificationTasks(contractId).catch(() => {
      // non-blocking
    });
  }, [contractId, contract?.isRatified, ensurePostRatificationTasks]);

  const timelineEvents = React.useMemo(() => {
    if (!contract) return [];
    return calculateContractTimeline(contract);
  }, [contract]);

  const tasks = React.useMemo(() => {
    const raw = contractTasksData?.data || [];
    const now = Date.now();
    return raw.map((t) => {
      const due = t.dueDate ? new Date(t.dueDate).getTime() : undefined;
      const isOverdue = Boolean(due && due < now && t.status !== 'completed');
      return {
        id: t._id,
        title: t.title,
        description: t.description || '',
        dueDate: t.dueDate || new Date().toISOString(),
        status: isOverdue
          ? 'overdue'
          : t.status === 'blocked'
          ? 'in_progress'
          : (t.status as Task['status']),
        assignedTo: t.assignedToRole,
        category: t.category || 'other',
        priority: t.priority,
      } as Task;
    });
  }, [contractTasksData]);

  // Calculate progress
  const completedEvents = timelineEvents.filter(
    (event) => event.status === 'completed'
  ).length;
  const totalEvents = timelineEvents.length;
  const progress = totalEvents > 0 ? (completedEvents / totalEvents) * 100 : 0;

  // Get upcoming deadlines
  const upcomingEvents = timelineEvents
    .filter(
      (event) => event.status === 'pending' || event.status === 'in_progress'
    )
    .filter(
      (event) =>
        new Date(event.dueDate) <=
        new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
    )
    .slice(0, 3);

  // Get overdue items
  const overdueItems = [
    ...timelineEvents.filter((event) => event.status === 'overdue'),
    ...tasks
      .filter((task) => task.status === 'overdue')
      .map((task) => ({
        id: task.id,
        title: task.title,
        description: task.description,
        dueDate: task.dueDate,
        status: task.status as
          | 'completed'
          | 'pending'
          | 'overdue'
          | 'in_progress',
        type: 'other' as const,
        assignedTo: task.assignedTo as 'buyer' | 'seller' | 'agent' | 'all',
        priority: 'medium' as const,
        completedDate: undefined,
      })),
  ];
  // Only the viewer's own overdue items are urgent; the other side's are
  // shown for information (a seller can't pay the buyer's earnest money).
  const myOverdueItems = overdueItems.filter(
    (item) => item.assignedTo === userRole || item.assignedTo === 'all'
  );
  const othersOverdueCount = overdueItems.length - myOverdueItems.length;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'default';
      case 'in_progress':
        return 'primary';
      case 'overdue':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle />;
      case 'pending':
        return <Schedule />;
      case 'in_progress':
        return <Assignment />;
      case 'overdue':
        return <Warning />;
      default:
        return <Schedule />;
    }
  };

  const handleExtensionRequest = async () => {
    if (!contractId || !extensionReason.trim() || requestedDays <= 0) {
      toast.error('Provide a reason and a valid number of days.');
      return;
    }
    try {
      const newDeadline = new Date();
      newDeadline.setDate(newDeadline.getDate() + requestedDays);
      await createAddendum({
        contractId,
        title: `Extension Request: ${requestedDays} days`,
        description: extensionReason,
        type: 'extension',
        createdBy: userRole,
        extensionDetails: {
          requestedExtensionDays: requestedDays,
          reason: extensionReason,
          newDeadline: newDeadline.toISOString().slice(0, 10),
          affectedMilestones: [],
        },
      }).unwrap();
      toast.success('Extension request submitted');
      setExtensionDialogOpen(false);
      setExtensionReason('');
      setRequestedDays(7);
    } catch (error) {
      console.error('Failed to create extension request:', error);
      toast.error('Failed to submit extension request.');
    }
  };

  const messageRecipientOptions = React.useMemo(() => {
    const roles: Array<{ value: 'buyer' | 'seller' | 'agent'; label: string }> =
      [];
    if (userRole !== 'buyer') roles.push({ value: 'buyer', label: 'Buyer' });
    if (userRole !== 'seller') roles.push({ value: 'seller', label: 'Seller' });
    if (userRole !== 'agent') roles.push({ value: 'agent', label: 'Agent' });
    return roles;
  }, [userRole]);

  React.useEffect(() => {
    if (!messageDialogOpen) return;
    if (quickMessageToRole) return;
    if (messageRecipientOptions.length > 0) {
      setQuickMessageToRole(messageRecipientOptions[0].value);
    }
  }, [messageDialogOpen, quickMessageToRole, messageRecipientOptions]);

  const handleSendMessage = async () => {
    const text = newMessage.trim();
    if (!text) {
      toast.error('Please enter a message.');
      return;
    }
    if (!quickMessageToRole) {
      toast.error('Select who should receive this message.');
      return;
    }
    try {
      await sendMessage({
        contractId,
        toRole: quickMessageToRole,
        message: text,
      }).unwrap();
      toast.success('Message sent');
      setMessageDialogOpen(false);
      setNewMessage('');
    } catch (e: unknown) {
      const err = e as { data?: { message?: string } };
      toast.error(err?.data?.message || 'Failed to send message');
    }
  };

  if (contractLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Typography>Loading dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '1200px', margin: '0 auto', p: 2 }}>
      {/* Header */}
      <GlassCard variant="subtle" sx={{ mb: 3 }}>
        <CardHeader
          sx={{
            pb: 2,
            borderBottom: `1px solid ${theme.palette.greyShades.border1}`,
          }}
          avatar={
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              {userRole === 'buyer' ? (
                <Person />
              ) : userRole === 'seller' ? (
                <Home />
              ) : (
                <Business />
              )}
            </Avatar>
          }
          title={
            <Typography
              variant="h4"
              component="h1"
              sx={{ color: theme.palette.text.primary, fontWeight: 700 }}
            >
              {userRole === 'buyer' ? 'Purchase Dashboard' : 'Sale Dashboard'}
            </Typography>
          }
          subheader={
            <Box sx={{ mt: 0.5 }}>
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                  fontSize: { xs: '1rem', sm: '1.15rem' },
                }}
              >
                {contract?.sellerPropertyInfo?.propertyAddress ||
                  contract?.product?.address?.fullAddress ||
                  [
                    contract?.product?.address?.street,
                    contract?.product?.address?.city,
                    contract?.product?.address?.state,
                    contract?.product?.address?.zip,
                  ]
                    .filter(Boolean)
                    .join(', ') ||
                  'Property Address'}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary, mt: 0.5 }}
              >
                Contract ID: {contractId} |{' '}
                {userRole.charAt(0).toUpperCase() + userRole.slice(1)}:{' '}
                {userName}
              </Typography>
            </Box>
          }
          action={
            <Stack
              direction="row"
              spacing={1}
              sx={{
                '& .MuiIconButton-root': {
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    bgcolor: theme.palette.action.hover,
                    color: theme.palette.primary.main,
                  },
                },
              }}
            >
              <Tooltip title="Create Addendum">
                <IconButton onClick={() => setAddendumDialogOpen(true)}>
                  <Add />
                </IconButton>
              </Tooltip>
              <Tooltip title="Send Message">
                <IconButton onClick={() => setMessageDialogOpen(true)}>
                  <Badge
                    badgeContent={unreadMessageCount}
                    color="error"
                    invisible={unreadMessageCount === 0}
                  >
                    <Message />
                  </Badge>
                </IconButton>
              </Tooltip>
              <Tooltip title="Request Extension">
                <IconButton onClick={() => setExtensionDialogOpen(true)}>
                  <Extension />
                </IconButton>
              </Tooltip>
              <Tooltip title="Notifications">
                <IconButton>
                  <Badge badgeContent={myOverdueItems.length} color="error">
                    <Notifications />
                  </Badge>
                </IconButton>
              </Tooltip>
            </Stack>
          }
        />
      </GlassCard>

      {/* Progress Overview */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <GlassCard variant="subtle">
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: theme.palette.text.primary, fontWeight: 600 }}
              >
                Transaction Progress
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{ height: 8, borderRadius: 4, mb: 2 }}
              />
              <Typography
                variant="body2"
                sx={{ color: theme.palette.greyShades.grey1 }}
              >
                {completedEvents} of {totalEvents} milestones completed
              </Typography>
            </CardContent>
          </GlassCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <GlassCard variant="subtle">
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: theme.palette.text.primary, fontWeight: 600 }}
              >
                Days to Closing
              </Typography>
              <Typography
                variant="h3"
                sx={{ color: theme.palette.mainColors.primary }}
              >
                {daysUntil(contract?.offerSpecifics?.closingDate) ?? 'TBD'}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.greyShades.grey1 }}
              >
                Target closing date
              </Typography>
            </CardContent>
          </GlassCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <GlassCard variant="subtle">
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: theme.palette.text.primary, fontWeight: 600 }}
              >
                Pending Tasks
              </Typography>
              <Typography
                variant="h3"
                sx={{ color: theme.palette.mainColors.secondary }}
              >
                {tasksQueryLoading
                  ? '…'
                  : tasks.filter(
                      (task) =>
                        task.status === 'pending' ||
                        task.status === 'in_progress'
                    ).length}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.greyShades.grey1 }}
              >
                {tasksQueryLoading
                  ? 'Loading tasks…'
                  : `${
                      tasks.filter((task) => task.status === 'overdue').length
                    } overdue`}
              </Typography>
              {!tasksQueryLoading &&
                tasks.length === 0 &&
                contract?.isRatified && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      display: 'block',
                      mt: 1,
                    }}
                  >
                    No tasks yet. Open the Tasks tab to add items, or wait for
                    default tasks to be created.
                  </Typography>
                )}
            </CardContent>
          </GlassCard>
        </Grid>
      </Grid>

      {/* Pipeline Overview (real docs/tasks gating) */}
      {pipelineData?.data && (
        <GlassCard variant="subtle" sx={{ mb: 3 }}>
          <CardContent>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              <Typography
                variant="h6"
                sx={{ color: theme.palette.text.primary, fontWeight: 600 }}
              >
                Pipeline Status
              </Typography>
              <Chip
                label={
                  pipelineData.data.stages.find((s) => s.isCurrent)?.label ||
                  pipelineData.data.currentStageKey
                }
                color="primary"
                size="small"
              />
            </Box>
            <LinearProgress
              variant="determinate"
              value={
                pipelineData.data.stages.find((s) => s.isCurrent)?.progress
                  .percent ?? 0
              }
              sx={{ height: 8, borderRadius: 4, mb: 1 }}
            />
            <Typography
              variant="body2"
              sx={{ color: theme.palette.greyShades.grey1 }}
            >
              Blockers:{' '}
              {pipelineData.data.stages.find((s) => s.isCurrent)?.progress
                .blockers.length ?? 0}
            </Typography>
          </CardContent>
        </GlassCard>
      )}

      {/* Alerts */}
      {userRole === 'seller' &&
        contract?.buyerSign?.isStored &&
        !contract?.sellerSign?.isStored &&
        (contract?.contractStatus === 'awaiting_seller_signature' ||
          contract?.contractStatus === 'awaiting_signatures') && (
          <Alert
            severity="warning"
            sx={{ mb: 3 }}
            action={
              <Button
                component={RouterLink}
                to={`/seller/contract/${contractId}/sign`}
                variant="contained"
                color="warning"
                size="small"
              >
                Sign contract
              </Button>
            }
          >
            <Typography variant="h6">Your signature is needed</Typography>
            <Typography variant="body2">
              The buyer has signed. The contract is ratified once you sign.
            </Typography>
          </Alert>
        )}
      {myOverdueItems.length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6">
            Urgent: {myOverdueItems.length} Overdue Items
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please review and complete overdue tasks and deadlines immediately.
          </Typography>
        </Alert>
      )}
      {othersOverdueCount > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {othersOverdueCount} item(s) owed by the other parties are past due.
          </Typography>
        </Alert>
      )}

      {upcomingEvents.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="h6">Upcoming Deadlines</Typography>
          <Typography variant="body2" color="text.secondary">
            {upcomingEvents.length} deadline(s) approaching within 7 days.
          </Typography>
        </Alert>
      )}

      {/* Main Dashboard Tabs */}
      <GlassCard variant="medium">
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{
              '& .MuiTab-root': {
                color: theme.palette.text.secondary,
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: theme.palette.primary.main,
              },
            }}
          >
            <Tab label="Timeline" icon={<Timeline />} />
            <Tab label="Tasks" icon={<Assignment />} />
            <Tab label="Documents" icon={<Description />} />
            <Tab label="Communication" icon={<Message />} />
            <Tab label="Addendums" icon={<Gavel />} />
            <Tab label="Activity" icon={<Notifications />} />
            {contract?.contingency?.inspectionContingency ===
              'reserve_the_right' && (
              <Tab label="Inspection & Property" icon={<Visibility />} />
            )}
            {userRole === 'agent' && (
              <Tab label="Closing Coordination" icon={<Business />} />
            )}
          </Tabs>
        </Box>

        {/* Timeline Tab */}
        <TabPanel value={activeTab} index={0}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{ color: theme.palette.text.primary, fontWeight: 600 }}
          >
            Transaction Timeline
          </Typography>
          <Stepper orientation="vertical">
            {timelineEvents.map((event, index) => {
              const daysUntil = getDaysUntilDeadline(event.dueDate);
              const isOverdue = event.status === 'overdue';
              const isInProgress = event.status === 'in_progress';

              return (
                <Step key={event.id} completed={event.status === 'completed'}>
                  <StepLabel
                    icon={getStatusIcon(event.status)}
                    optional={
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        Due: {formatTimelineDate(event.dueDate)}
                      </Typography>
                    }
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      flexWrap="wrap"
                    >
                      <Typography
                        variant="h6"
                        sx={{ color: theme.palette.text.primary }}
                      >
                        {event.title}
                      </Typography>
                      <Chip
                        label={event.status.replace('_', ' ')}
                        color={
                          isOverdue
                            ? 'error'
                            : isInProgress
                            ? 'warning'
                            : 'default'
                        }
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                      <Chip
                        label={event.assignedTo}
                        color="default"
                        size="small"
                        variant="outlined"
                      />
                      {event.extensionDays && (
                        <Chip
                          label={`+${event.extensionDays} days`}
                          color="info"
                          size="small"
                          icon={<Extension />}
                        />
                      )}
                    </Box>
                  </StepLabel>
                  <StepContent>
                    <Typography
                      variant="body2"
                      sx={{ mb: 2, color: theme.palette.text.secondary }}
                    >
                      {event.description}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      {daysUntil > 0 && (
                        <Typography
                          variant="caption"
                          sx={{ color: theme.palette.success.main }}
                        >
                          {daysUntil} days remaining
                        </Typography>
                      )}
                      {isOverdue && (
                        <Typography
                          variant="caption"
                          sx={{ color: 'error.main' }}
                        >
                          {Math.abs(daysUntil)} days overdue
                        </Typography>
                      )}
                    </Box>
                    {event.extensionReason && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontStyle: 'italic',
                        }}
                      >
                        Extension reason: {event.extensionReason}
                      </Typography>
                    )}
                    <Box>
                      <GlassButton size="small" startIcon={<Visibility />}>
                        View Details
                      </GlassButton>
                      {event.assignedTo === userRole &&
                        event.status !== 'completed' && (
                          <GlassButton
                            size="small"
                            startIcon={<Edit />}
                            sx={{ ml: 1 }}
                          >
                            Update Status
                          </GlassButton>
                        )}
                    </Box>
                  </StepContent>
                </Step>
              );
            })}
          </Stepper>
        </TabPanel>

        {/* Tasks Tab */}
        <TabPanel value={activeTab} index={1}>
          <TaskManagement userRole={userRole} contractId={contractId} />
        </TabPanel>

        {/* Documents Tab */}
        <TabPanel value={activeTab} index={2}>
          <DocumentCenter userRole={userRole} contractId={contractId} />
        </TabPanel>

        {/* Communication Tab */}
        <TabPanel value={activeTab} index={3}>
          <CommunicationHub
            userRole={userRole}
            userName={userName}
            contractId={contractId}
          />
        </TabPanel>

        {/* Addendums Tab */}
        <TabPanel value={activeTab} index={4}>
          <AddendumInterface
            contractId={contractId}
            userRole={userRole}
            contractStatus={contract?.contractStatus || ''}
          />
        </TabPanel>

        {/* Activity Tab */}
        <TabPanel value={activeTab} index={5}>
          <ActivityFeed contractId={contractId} />
        </TabPanel>

        {/* Inspection & Property Tab */}
        {contract?.contingency?.inspectionContingency ===
          'reserve_the_right' && (
          <TabPanel value={activeTab} index={6}>
            <InspectionProperty
              userRole={userRole}
              userName={userName}
              contractId={contractId}
            />
          </TabPanel>
        )}

        {/* Transaction Coordinator Tab (Agent Only) */}
        {userRole === 'agent' && (
          <TabPanel
            value={activeTab}
            index={
              contract?.contingency?.inspectionContingency ===
              'reserve_the_right'
                ? 7
                : 6
            }
          >
            <ClosingCoordination userRole={userRole} contractId={contractId} />
          </TabPanel>
        )}
      </GlassCard>

      {/* Extension Request Dialog */}
      <Dialog
        open={extensionDialogOpen}
        onClose={() => setExtensionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Request Extension</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Extension Reason"
            fullWidth
            multiline
            rows={4}
            value={extensionReason}
            onChange={(e) => setExtensionReason(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Requested Days"
            type="number"
            fullWidth
            value={requestedDays}
            onChange={(e) => setRequestedDays(parseInt(e.target.value))}
          />
        </DialogContent>
        <DialogActions>
          <GlassButton onClick={() => setExtensionDialogOpen(false)}>
            Cancel
          </GlassButton>
          <GlassButton onClick={handleExtensionRequest} glassVariant="medium">
            Request Extension
          </GlassButton>
        </DialogActions>
      </Dialog>

      {/* Message Dialog */}
      <Dialog
        open={messageDialogOpen}
        onClose={() => setMessageDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Message</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <InputLabel id="quick-msg-recipient">To</InputLabel>
            <Select
              labelId="quick-msg-recipient"
              label="To"
              value={quickMessageToRole}
              onChange={(e) =>
                setQuickMessageToRole(
                  e.target.value as 'buyer' | 'seller' | 'agent'
                )
              }
            >
              {messageRecipientOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            autoFocus
            margin="dense"
            label="Message"
            fullWidth
            multiline
            rows={4}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <GlassButton onClick={() => setMessageDialogOpen(false)}>
            Cancel
          </GlassButton>
          <GlassButton
            onClick={() => void handleSendMessage()}
            glassVariant="medium"
            disabled={sendMessageLoading}
          >
            Send Message
          </GlassButton>
        </DialogActions>
      </Dialog>

      {/* Create Addendum Dialog */}
      <CreateAddendumForm
        open={addendumDialogOpen}
        onClose={() => setAddendumDialogOpen(false)}
        contractId={contractId}
        userRole={userRole}
      />
    </Box>
  );
};

export default PostAgreementDashboard;
